"use client";

import NProgress from "nprogress";
import { useEffect, useRef } from "react";

/**
 * Shows the top-of-page progress bar while a query is refetching
 * (filter/sort change), but NOT on initial load or infinite scroll.
 *
 * NProgress.start() is deferred with setTimeout(0) because nextjs-toploader
 * monkey-patches history.pushState/replaceState to call NProgress.done()
 * synchronously — URL-param filter changes would otherwise kill the bar
 * before the fetch completes.
 */
export function useTopLoaderOnRefetch(isRefetching: boolean) {
  const started = useRef(false);
  const startedAt = useRef<number | null>(null);
  const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishFrames = useRef<number[]>([]);

  useEffect(() => {
    const clearFinish = () => {
      if (finishTimer.current) clearTimeout(finishTimer.current);
      finishTimer.current = null;
      for (const frame of finishFrames.current) cancelAnimationFrame(frame);
      finishFrames.current = [];
    };

    clearFinish();

    if (isRefetching) {
      const id = setTimeout(() => {
        NProgress.start();
        started.current = true;
        startedAt.current = performance.now();
      }, 0);
      return () => clearTimeout(id);
    }
    if (started.current) {
      const elapsed = startedAt.current ? performance.now() - startedAt.current : 0;
      const delay = Math.max(0, 250 - elapsed);

      finishTimer.current = setTimeout(() => {
        const firstFrame = requestAnimationFrame(() => {
          const secondFrame = requestAnimationFrame(() => {
            NProgress.done();
            started.current = false;
            startedAt.current = null;
            finishFrames.current = [];
          });
          finishFrames.current.push(secondFrame);
        });
        finishFrames.current.push(firstFrame);
      }, delay);
    }

    return clearFinish;
  }, [isRefetching]);
}
